import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Send, Loader2, Bot, ChevronUp, ChevronDown } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface TaxReturnChatbotProps {
  type: "trends" | "compare";
  data: unknown;
  dataReady: boolean;
  defaultCollapsed?: boolean;
  resizable?: boolean;
}

const MIN_BODY_HEIGHT = 140;
const MAX_BODY_HEIGHT = 460;
const DEFAULT_BODY_HEIGHT = 220;

function InlineMarkdown({ text }: { text: string }) {
  const parts: React.ReactNode[] = [];
  const re = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
  let last = 0, m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[2]) parts.push(<strong key={m.index}>{m[2]}</strong>);
    else if (m[3]) parts.push(<em key={m.index}>{m[3]}</em>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

function BulletText({ text }: { text: string }) {
  const lines = text.split("\n").filter(l => l.trim());
  return (
    <div className="space-y-2.5">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        const isBullet = trimmed.startsWith("•") || trimmed.startsWith("-") || /^\d+\./.test(trimmed);
        const content = isBullet ? trimmed.replace(/^[•\-]\s*/, "").replace(/^\d+\.\s*/, "") : trimmed;
        return (
          <p key={i} className={`text-sm leading-relaxed ${isBullet ? "pl-4 relative before:content-['•'] before:absolute before:left-0 before:text-primary/60" : ""}`}>
            <InlineMarkdown text={content} />
          </p>
        );
      })}
    </div>
  );
}

export function TaxReturnChatbot({ type, data, dataReady, defaultCollapsed = false, resizable = false }: TaxReturnChatbotProps) {
  const { t } = useTranslation(["financial", "common"]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [bodyHeight, setBodyHeight] = useState(DEFAULT_BODY_HEIGHT);
  const initialized = useRef(false);
  const userSent = useRef(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const lastMsgRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragState = useRef<{ pointerId: number; startY: number; startHeight: number } | null>(null);

  function onResizeStart(e: React.PointerEvent) {
    if (!resizable) return;
    e.preventDefault();
    e.stopPropagation();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    dragState.current = {
      pointerId: e.pointerId,
      startY: e.clientY,
      startHeight: collapsed ? 0 : bodyHeight,
    };
  }

  function onResizeMove(e: React.PointerEvent) {
    const drag = dragState.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const delta = drag.startY - e.clientY; // dragging up => positive
    const next = drag.startHeight + delta;
    if (next < MIN_BODY_HEIGHT * 0.5) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
      setBodyHeight(Math.min(MAX_BODY_HEIGHT, Math.max(MIN_BODY_HEIGHT, next)));
    }
  }

  function onResizeEnd(e: React.PointerEvent) {
    const drag = dragState.current;
    if (drag && drag.pointerId === e.pointerId) {
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
      dragState.current = null;
    }
  }

  function retry() {
    initialized.current = false;
    setMessages([]);
    setError(null);
    runInitial();
  }

  useEffect(() => {
    if (!dataReady || initialized.current) return;
    initialized.current = true;
    runInitial();
  }, [dataReady]);

  useEffect(() => {
    if (!userSent.current) return;
    // Bring the TOP of the latest message into view so a long response is read
    // from its beginning instead of jumping straight to the bottom.
    lastMsgRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [messages, loading]);

  async function runInitial() {
    setInitializing(true);
    setError(null);
    try {
      const res = await apiRequest("POST", "/api/tax/chat", { type, data, messages: [] });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to generate insights");
      setMessages([{ role: "assistant", content: json.reply }]);
    } catch (e: any) {
      setError(e.message ?? "Could not load insights. Check that your ANTHROPIC_TAX_CHAT_KEY is configured.");
    } finally {
      setInitializing(false);
    }
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setError(null);

    const nextMessages: Message[] = [...messages, { role: "user", content: text }];
    userSent.current = true;
    setMessages(nextMessages);
    setLoading(true);

    try {
      const res = await apiRequest("POST", "/api/tax/chat", { type, data, messages: nextMessages });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to get response");
      setMessages(prev => [...prev, { role: "assistant", content: json.reply }]);
    } catch (e: any) {
      setError(e.message ?? "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }

  const hasActivity = initializing || messages.length > 0 || !!error;

  return (
    <div className="flex flex-col border-t-2 border-primary/30 bg-primary/5 shrink-0">
      {/* Drag handle — only when resizable; drag up to grow, down to collapse */}
      {resizable && (
        <div
          onPointerDown={onResizeStart}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeEnd}
          onPointerCancel={onResizeEnd}
          className="group flex items-center justify-center h-3 cursor-ns-resize bg-primary/10 hover:bg-primary/20 transition-colors shrink-0 touch-none select-none"
          style={{ touchAction: "none" }}
          title={t("financial:taxChat.dragToResize")}
          data-testid="handle-tax-chat-resize"
        >
          <div className="w-10 h-1 rounded-full bg-primary/40 group-hover:bg-primary/60 transition-colors" />
        </div>
      )}
      {/* Header — always visible, clickable to toggle */}
      <button
        type="button"
        onClick={() => setCollapsed(c => !c)}
        className="flex items-center gap-2 px-4 py-2 border-b border-primary/20 bg-primary/10 hover:bg-primary/15 transition-colors w-full text-left shrink-0"
        data-testid="button-tax-chat-toggle"
      >
        <div className="w-5 h-5 rounded-md bg-primary/20 flex items-center justify-center shrink-0">
          {initializing
            ? <Loader2 className="w-3 h-3 text-primary animate-spin" />
            : <Bot className="w-3 h-3 text-primary" />}
        </div>
        <div className="flex-1 min-w-0 flex items-center gap-1.5">
          <span className="text-xs font-semibold text-primary">{t("financial:taxChat.askAi")}</span>
          {!collapsed && <span className="text-[10px] text-muted-foreground">{t("financial:taxChat.factsOnly")}</span>}
          {collapsed && hasActivity && (
            <span className="text-[10px] text-primary/60 italic">
              {initializing ? "analyzing…" : messages.length > 0 ? "ready" : error ? "error" : ""}
            </span>
          )}
        </div>
        {collapsed
          ? <ChevronUp className="w-3.5 h-3.5 text-primary/60 shrink-0" />
          : <ChevronDown className="w-3.5 h-3.5 text-primary/60 shrink-0" />}
      </button>

      {!collapsed && (
        <>
          <div className="overflow-y-auto px-5 py-3 space-y-3" style={resizable ? { height: bodyHeight } : { maxHeight: 220 }}>
            {initializing && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span className="text-xs">{t("financial:taxChat.analyzing")}</span>
              </div>
            )}

            {!initializing && messages.length === 0 && !error && (
              <p className="text-xs text-muted-foreground">{t("financial:taxChat.insightsPlaceholder")}</p>
            )}

            {messages.map((msg, i) => (
              <div
                key={i}
                ref={i === messages.length - 1 ? lastMsgRef : undefined}
                className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {msg.role === "assistant" && (
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Bot className="w-3 h-3 text-primary" />
                  </div>
                )}
                <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm leading-snug ${
                  msg.role === "assistant"
                    ? "bg-background border border-border text-foreground"
                    : "bg-primary text-primary-foreground"
                }`}>
                  {msg.role === "assistant" ? <BulletText text={msg.content} /> : msg.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex gap-2 justify-start">
                <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Bot className="w-3 h-3 text-primary" />
                </div>
                <div className="bg-background border border-border rounded-lg px-3 py-2 flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Thinking…</span>
                </div>
              </div>
            )}

            {error && (
              <div className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded px-3 py-2 flex items-center justify-between gap-2">
                <span>{error}</span>
                <button
                  onClick={retry}
                  className="shrink-0 text-[10px] underline text-destructive hover:opacity-70 transition-opacity"
                  data-testid="button-tax-chat-retry"
                >
                  Retry
                </button>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          <div className="shrink-0 px-4 py-2.5 border-t border-border">
            <form
              onSubmit={e => { e.preventDefault(); sendMessage(); }}
              className="flex items-center gap-2"
            >
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder={t("financial:taxChat.followUpPlaceholder")}
                disabled={loading || initializing}
                className="flex-1 h-8 px-3 text-xs rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                data-testid="input-tax-chat"
              />
              <button
                type="submit"
                disabled={!input.trim() || loading || initializing}
                className="h-8 w-8 flex items-center justify-center rounded-md bg-primary text-primary-foreground disabled:opacity-40 hover:bg-primary/90 transition-colors shrink-0"
                data-testid="button-tax-chat-send"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
